import type { CrossTabCatalogModeBusy } from 'pages/Robot/RobotView/RobotLeftBlock/Service/catalogServiceCrossTab'
import { parseCatalogServiceItemKeyFromActionKey } from 'pages/Robot/RobotView/RobotLeftBlock/Service/catalogServiceCrossTab'
import {
  isRobotCatalogServiceActive,
  parseCatalogCalibItemKeyFromActionKey,
} from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibLotService'
import type { ServicePointCalibrationStatus } from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibTypes'
import { serviceCalibAwaitOutcomeMatches } from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibUtils'
import { ROBOT_MODES } from 'utils/constants'
import type { IModeSwitchPending, ServiceCalibAwaitOutcome } from 'utils/interfaces'

/** Safety net while robot completes catalog operation (MR-04o). */
export const CATALOG_OPERATION_TIMEOUT_MS = 90_000
/** After Finish (mode 0), wait for onboard ready before treating calib as failed. */
export const CALIB_FINISH_AWAIT_TIMEOUT_MS = 10_000
export const MODE_SWITCH_TIMEOUT_PADDING_MS = 5_000

export type LocalCatalogDispatch = {
  mode: ROBOT_MODES
  actionKey: string
  awaitCalib?: ServiceCalibAwaitOutcome
}

export function resolveLocalCatalogDispatch(
  optimistic: LocalCatalogDispatch | null | undefined,
  crossTabBusy: CrossTabCatalogModeBusy | null
): LocalCatalogDispatch | null {
  if (optimistic) {
    return optimistic
  }
  if (!crossTabBusy) {
    return null
  }
  return {
    mode: crossTabBusy.mode,
    actionKey: crossTabBusy.actionKey,
    awaitCalib: crossTabBusy.awaitCalib,
  }
}

function isCatalogServiceStopAction(actionKey: string): boolean {
  return actionKey.startsWith('catalog-service-stop:')
}

function isCatalogServiceStartAction(actionKey: string): boolean {
  return actionKey.startsWith('catalog-service:') && !actionKey.includes('stop')
}

export function isCatalogCalibStartAction(actionKey: string): boolean {
  return actionKey.startsWith('catalog-calib:') && !actionKey.includes('finish')
}

export function isCatalogCalibFinishAction(actionKey: string): boolean {
  return actionKey.startsWith('catalog-calib-finish:')
}

export function isCatalogResetAction(actionKey: string): boolean {
  return actionKey.startsWith('catalog-reset:')
}

/**
 * After a frontend reload Redux optimistic is lost but localStorage cross-tab busy may remain.
 * Clear when robot feedback/pending no longer matches the stored operation.
 */
export function shouldClearStaleCrossTabCatalogBusy(
  busy: CrossTabCatalogModeBusy,
  activeMode: ROBOT_MODES | null | undefined,
  serverPending: IModeSwitchPending | null | undefined
): boolean {
  const pendingMode = serverPending?.mode

  if (isCatalogCalibStartAction(busy.actionKey)) {
    // Already in mode 15 — start completed; clear so UI can show Finish from robot-held state.
    if (activeMode === ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT) {
      return true
    }
    if (pendingMode === ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT) {
      return false
    }
    return true
  }

  if (isCatalogCalibFinishAction(busy.actionKey)) {
    if (activeMode === ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT) {
      return false
    }
    if (pendingMode === ROBOT_MODES.MODE_NOT_SET) {
      return false
    }
    return true
  }

  if (isCatalogResetAction(busy.actionKey)) {
    if (activeMode === ROBOT_MODES.MODE_RESET_CALIBRATION_SERVICE_POINT) {
      return false
    }
    if (pendingMode === ROBOT_MODES.MODE_RESET_CALIBRATION_SERVICE_POINT) {
      return false
    }
    return true
  }

  if (busy.mode === ROBOT_MODES.MODE_SERVICE_POINT || isCatalogServiceStartAction(busy.actionKey)) {
    // Already in mode 17 — start completed; clear so UI can show Stop from robot-held state.
    if (activeMode === ROBOT_MODES.MODE_SERVICE_POINT && isCatalogServiceStartAction(busy.actionKey)) {
      return true
    }
    if (activeMode === ROBOT_MODES.MODE_SERVICE_POINT) {
      return false
    }
    if (pendingMode === ROBOT_MODES.MODE_SERVICE_POINT) {
      return false
    }
    return true
  }

  if (busy.mode === ROBOT_MODES.MODE_NOT_SET || isCatalogServiceStopAction(busy.actionKey)) {
    if (activeMode === ROBOT_MODES.MODE_SERVICE_POINT) {
      return false
    }
    if (pendingMode === ROBOT_MODES.MODE_SERVICE_POINT) {
      return false
    }
    return true
  }

  return true
}

export type CatalogConfirmedRowKeys = {
  calibKey: string | null
  serviceKey: string | null
}

/**
 * Dispatch settled when robot feedback / onboard match the expected outcome.
 * - calibrate / action start → confirmed mode in feedback (15 / 17) for this catalog row
 * - reset / finish / stop exit → onboard or feedback per action kind
 */
export function isLocalCatalogDispatchSettled(
  dispatch: LocalCatalogDispatch | null,
  calibrationRows: ServicePointCalibrationStatus[] | undefined | null,
  activeMode: ROBOT_MODES | null | undefined,
  serverPending: IModeSwitchPending | null | undefined,
  confirmedKeys?: CatalogConfirmedRowKeys
): boolean {
  if (!dispatch) {
    return true
  }

  if (dispatch.awaitCalib && isCatalogCalibFinishAction(dispatch.actionKey)) {
    return serviceCalibAwaitOutcomeMatches(dispatch.awaitCalib, calibrationRows)
  }

  if (dispatch.awaitCalib && isCatalogResetAction(dispatch.actionKey)) {
    return serviceCalibAwaitOutcomeMatches(dispatch.awaitCalib, calibrationRows)
  }

  if (isCatalogCalibStartAction(dispatch.actionKey)) {
    const expectedKey = parseCatalogCalibItemKeyFromActionKey(dispatch.actionKey)
    return (
      activeMode === ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT &&
      expectedKey != null &&
      confirmedKeys?.calibKey === expectedKey
    )
  }

  if (dispatch.mode === ROBOT_MODES.MODE_SERVICE_POINT && isCatalogServiceStartAction(dispatch.actionKey)) {
    const expectedKey = parseCatalogServiceItemKeyFromActionKey(dispatch.actionKey)
    return (
      activeMode === ROBOT_MODES.MODE_SERVICE_POINT &&
      expectedKey != null &&
      confirmedKeys?.serviceKey === expectedKey
    )
  }

  if (dispatch.mode === ROBOT_MODES.MODE_NOT_SET || isCatalogServiceStopAction(dispatch.actionKey)) {
    return !isRobotCatalogServiceActive(activeMode, serverPending)
  }

  if (dispatch.awaitCalib) {
    return serviceCalibAwaitOutcomeMatches(dispatch.awaitCalib, calibrationRows)
  }

  return false
}
